import { useCallback, useMemo, useState } from 'preact/hooks';
import { UserFilters } from '@core/configs/admin/users/users-table.config';
import { normalizeUserSearchOption } from '@core/utils/filters/normalize-search-option';

export function useUserFilter() {
    const [sorting, setSorting] = useState(() => ({
        sortByColumn: UserFilters?.sorting?.sortByColumn ?? 'firstName',
        sortByDescending: UserFilters?.sorting?.sortByDescending ?? false,
    }));

    const [pagination, setPagination] = useState(() => ({
        pageNumber: UserFilters?.pagination?.paginationData?.pageNumber ?? 1,
        pageSize: UserFilters?.pagination?.paginationData?.pageSize ?? 10,
    }));

    const [search, setSearch] = useState(() => ({
        searchTerm: UserFilters?.search?.searchTerm ?? '',
        searchOption: normalizeUserSearchOption(UserFilters?.search?.searchOption),
    }));

    const [filters, setFilters] = useState(() => ({
        role: UserFilters?.filters?.role ?? '',
        status: UserFilters?.filters?.status,
    }));

    const requestParams = useMemo(() => {
        return {
            pageNumber: pagination.pageNumber,
            pageSize: pagination.pageSize,
            sortByColumn: sorting.sortByColumn,
            sortByDescending: sorting.sortByDescending,
            ...(search.searchTerm ? { searchTerm: search.searchTerm } : {}),
            ...(search.searchOption ? { searchOption: search.searchOption } : {}),
            ...(filters ? filters : {}),
        };
    }, [
        pagination.pageNumber,
        pagination.pageSize,
        sorting.sortByColumn,
        sorting.sortByDescending,
        search.searchTerm,
        search.searchOption,
        filters.roleId,
        filters.remoteSystemId,
        filters.includeDeleted,
    ]);

    const sortingCallback = useCallback((incoming) => {
        setSorting((prev) => ({ ...prev, ...incoming }));
        setPagination((prev) => ({ ...prev, pageNumber: 1 }));
    }, []);

    const paginationCallback = useCallback((incoming) => {
        setPagination((prev) => ({ ...prev, ...incoming }));
    }, []);

    const searchCallback = useCallback((searchTerm, searchOption) => {
        const normalizedSearchOption = normalizeUserSearchOption(searchOption);

        setSearch((prev) => ({
            ...prev,
            searchTerm,
            searchOption: normalizedSearchOption,
        }));
        setPagination((p) => ({ ...p, pageNumber: 1 }));
    }, []);

    function translateFilter(incoming) {
        const includeDeletedValue = incoming?.includeDeleted?.value;

        return {
            roleId: incoming?.roleId?.value ?? null,
            remoteSystemId: incoming?.remoteSystemId?.value ?? null,
            includeDeleted:
                includeDeletedValue === 0 ||
                includeDeletedValue === '0' ||
                includeDeletedValue === true,
        };
    }

    const filtersCallback = useCallback(
        (incoming) => {
            const incomingFilters = translateFilter(incoming);
            setFilters((prev) => ({ ...prev, ...incomingFilters }));
            setPagination((p) => ({ ...p, pageNumber: 1 }));
        },
        [translateFilter]
    );

    const tableFilters = useMemo(() => {
        const base = {
            ...UserFilters,
            sorting: {
                ...(UserFilters?.sorting ?? {}),
                ...sorting,
                callback: sortingCallback,
            },
            pagination: {
                ...(UserFilters?.pagination ?? {}),
                paginationData: { ...pagination },
                paginationEvent: paginationCallback,
            },
        };

        if (UserFilters?.advancedFilters) {
            base.advancedFilters = {
                config: {
                    ...(UserFilters.advancedFilters?.config || {}),
                },
                callbacks: {
                    search: searchCallback,
                    filter: filtersCallback,
                },
            };
        }

        return base;
    }, [
        searchCallback,
        filtersCallback,
        sortingCallback,
        paginationCallback,
        sorting.sortByColumn,
        sorting.sortByDescending,
        pagination.pageNumber,
        pagination.pageSize,
        filters.roleId,
        filters.remoteSystemId,
        filters.includeDeleted,
    ]);

    return {
        requestParams,
        tableFilters,
    };
}
